import React, { useState } from 'react';
import { Plus, X, Save, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ProjectCombobox } from '../ui/ProjectCombobox';
import { Project } from '@/hooks/useProjects';

interface AddTimeEntryFormProps {
  date: string;
  projects: Project[];
  onSave: (data: {
    projectId: string;
    startTime: string;
    endTime: string;
    description: string;
  }) => Promise<void>;
  onCancel: () => void;
}

export const AddTimeEntryForm: React.FC<AddTimeEntryFormProps> = ({
  date,
  projects,
  onSave,
  onCancel,
}) => {
  const [projectId, setProjectId] = useState('');
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('12:00');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId || !startTime || !endTime) return;

    setIsSaving(true);
    try {
      await onSave({
        projectId,
        startTime,
        endTime,
        description,
      });
      // Reset form
      setProjectId('');
      setStartTime('08:00');
      setEndTime('12:00');
      setDescription('');
    } finally {
      setIsSaving(false);
    }
  };

  const isValid = projectId && startTime && endTime && startTime < endTime;

  return (
    <form onSubmit={handleSubmit} className="bg-blue-50 rounded-lg p-4 space-y-3 border border-blue-200">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-sm text-blue-800">Nouvelle entrée</h4>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={onCancel}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-2">
        <Label htmlFor="project" className="text-xs">Projet *</Label>
        <ProjectCombobox
          projects={projects}
          value={projectId}
          onValueChange={setProjectId}
          placeholder="Choisir un projet"
          triggerClassName="h-9 text-sm"
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label htmlFor="startTime" className="text-xs">Début *</Label>
          <Input
            id="startTime"
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className="h-9 text-sm"
            required
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="endTime" className="text-xs">Fin *</Label>
          <Input
            id="endTime"
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            className="h-9 text-sm"
            required
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="description" className="text-xs">Description</Label>
        <Input
          id="description"
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optionnel)"
          className="h-9 text-sm"
        />
      </div>

      <div className="flex gap-2 pt-1">
        <Button
          type="submit"
          size="sm"
          disabled={!isValid || isSaving}
          className="flex-1"
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <Save className="h-4 w-4 mr-1" />
          )}
          Enregistrer
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          className="flex-1"
        >
          Annuler
        </Button>
      </div>
    </form>
  );
};

interface AddTimeEntryButtonProps {
  onClick: () => void;
}

export const AddTimeEntryButton: React.FC<AddTimeEntryButtonProps> = ({ onClick }) => {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      className="w-full border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
    >
      <Plus className="h-4 w-4 mr-1" />
      Ajouter une entrée
    </Button>
  );
};
